import Toggle from 'react-toggle'
import React, {useEffect, useState} from "react";
import {Button, Col, Form, Row} from "react-bootstrap";
import {FormLabelRequiredAsterisk} from "Components/Form/FormLabelRequiredAsterisk";
import {FileUpload} from "Components/FileUpload/FileUpload";
import {
    ACTIVITY_TYPE_LABEL_BY_IDS, ACTIVITY_TYPE_WEEKLY_ID,
    ACTIVITY_TYPE_YEARLY_ID,
    ActivityTypeSelect
} from "Components/Activities/ActivityTypeSelect";
import {AdminYearDayForm} from "Views/Activities/AdminYearDayForm";
import {useMutation, useQuery} from "react-query";
import {CenterServiceResponse, CenterServiceResponseItem, getMutipleCenterServices} from "Api/Query/CenterQuery";
import {Notificator} from "Services/Notificator/Notificator";
import {useDispatch} from "react-redux";
import {appLoading} from "Store/loadingSlice";
import {Time} from "Components/Clock/Time";
import {MoneyAmount} from "Components/Money/MoneyAmount";
import {ROUTE_PATHS} from "Config/Router/Routes";
import {SelectOptionType} from "Model/Select/SelectOption";
import {RichTextEditor} from "Components/RichTextEditor/RichTextEditor";
import {CenterMultiSelect} from "Components/Center/CenterMultiSelect";
import {DiscountMultiSelect} from "Components/Discount/DiscountMultiSelect";
import {AddYearSubmitDto} from "../Modalities/AddYearDayModalForm";
import {removeActivityMedia} from "../../Api/Mutation/ActivityMutation";
import {InscriptionValidationTypeSelect} from "../../Components/Activities/InscriptionValidationTypeSelect";
import {ActivityCategorySelect} from "Components/Activities/ActivityCategorySelect";
import {PaymentTypeSelect} from "Components/Activities/PaymentTypeSelect";

interface Props {
    onSubmit: (data: any, isValid: boolean) => void,
    defaultValues?: { [key: string]: any }
    isEdit?: boolean,
    activityId?: string
}

export const AdminActivityForm: React.FC<Props> = (props) => {

    const defaultFormData = {
        name: "",
        type: "",
        initDate: "",
        endDate: "",
        initPaymentDate: "",
        endPaymentDate: "",
        description: "",
        frontImage: "",
        backgroundImage: "",
        bodyImage: "",
        documents: [],
        yearDays: [],
        services: [],
        sessions: [],
        centerIds: [],
        centers: [],
        discountIds: [],
        discounts: [],
        cancellationLocked: false,
        canSelectMultipleYearDays: false,
        inscriptionValidationType: null,
        category: null,
        paymentType: null,
        quarterlyPayment: false,
    }
    const [formData, setFormData] = useState<{ [key: string]: any }>(defaultFormData)
    const [centerServices, setCenterServices] = useState<CenterServiceResponseItem[]>([])
    const [validationErrors, setValidationErrors] = useState<{ [key: string]: boolean }>({})
    const [submitted, setSubmitted] = useState<boolean>(false)
    const [cancellationLocked, setCancellationLocked] = useState<boolean>(false)
    const [canSelectMultipleYearDays, setCanSelectMultipleYearDays] = useState<boolean>(false)
    const [quarterlyPayment, setQuarterlyPayment] = useState<boolean>(false)

    const centerServicesQuery = useQuery({
        enabled: false,
        queryKey: ['adminCenterServicesQuery', formData.centerIds],
        queryFn: () => getMutipleCenterServices(formData.centerIds), // TODO: refactor
        onSuccess: (response: any) => {
            setCenterServices(response.data._result.items)
        }
    })

    useEffect(() => {
        if (formData.centerIds.length) {
            centerServicesQuery.refetch()
        } else {
            setCenterServices([])
        }
    }, [formData.centerIds])

    const dispatch = useDispatch()

    useEffect(() => {
        dispatch(appLoading({value: centerServicesQuery.isLoading}))
    }, [centerServicesQuery.isLoading])

    useEffect(() => {
        if (!submitted) {
            return
        }

        validate(false)
    }, [formData])

    const submit = () => {
        if (!submitted) {
            setSubmitted(true)
        }

        const isValidForm = validate()
        props.onSubmit(formData, isValidForm)
    }

    useEffect(() => {
        let newFormData: any = props.defaultValues ?? defaultFormData

        // centers
        if (props.defaultValues?.centers) {
            const centerOptions: SelectOptionType[] = []
            props.defaultValues?.centers.map((center: any) => {
                centerOptions.push({
                    label: center.name,
                    value: center.id
                })
            })
            newFormData = {...newFormData, centers: centerOptions}
            newFormData = {...newFormData, centerIds: props.defaultValues?.centerIds}
        }

        // discounts
        if (props.defaultValues?.discounts) {
            const options: SelectOptionType[] = []
            props.defaultValues?.discounts.map((item: any) => {
                options.push({
                    label: item.name,
                    value: item.id
                })
            })
            newFormData = {...newFormData, discounts: options}
            newFormData = {...newFormData, discountIds: props.defaultValues?.discountIds}
        }

        if (props.defaultValues?.cancellationLocked !== null) {
            setCancellationLocked(props.defaultValues?.cancellationLocked)
        }

        if (props.defaultValues?.canSelectMultipleYearDays !== null) {
            setCanSelectMultipleYearDays(props.defaultValues?.canSelectMultipleYearDays)
        }

        if (props.defaultValues?.quarterlyPayment !== null) {
            setQuarterlyPayment(props.defaultValues?.quarterlyPayment)
        }

        setFormData(newFormData)

    }, [props.defaultValues])

    const validate = (showAlert = true): boolean => {
        const errors: { [key: string]: boolean } = {}

        const requiredFields: string[] = [
            'name', 'type', 'centerIds', 'initDate', 'endDate', 'initPaymentDate', 'endPaymentDate', 'frontImage', 'category'
        ]

        requiredFields.map(field => {
            const value = formData[field]
            if (!value || (Array.isArray(value) && value.length === 0)) {
                errors[field] = true
            }
        })

        setValidationErrors(errors)

        if (Object.keys(errors).length === 0) {
            return true
        }

        if (showAlert) {
            Notificator.error('Los campos en rojo son obligatorios.', 'Error')
        }

        return false
    }

    const removeImageMutation = useMutation({
        mutationKey: ['removeActivityImage'],
        mutationFn: (imageId: string) => removeActivityMedia(imageId),
        onSuccess: () => {
            Notificator.success('Imagen eliminada correctamente.', 'Éxito')
            setFormData({
                ...formData,
                bodyImage: null,
                bodyImageSrc: null
            })
        }
    })

    const removeDocumentMutation = useMutation({
        mutationKey: ['removeActivityDoc'],
        mutationFn: (imageId: string) => removeActivityMedia(imageId),
        onSuccess: (_data, imageId: string) => {
            Notificator.success('Documento eliminado correctamente.', 'Éxito')
            setFormData({
                ...formData,
                documents: Object.values(formData.documents).filter((doc: any) => doc.id !== imageId)
            })
        }
    })

    const removeImage = (bodyImageId: string) => {
        if (!window.confirm("¿Está seguro que desea eliminar la imagen?")) {
            return
        }

        removeImageMutation.mutate(bodyImageId)
    }

    const deleteDocument = (id: string) => {
        if (!window.confirm("¿Está seguro que desea eliminar el documento?")) {
            return
        }

        removeDocumentMutation.mutate(id)
    }

    useEffect(() => {
        if (cancellationLocked !== formData.cancellationLocked) {
            setFormData({...formData, cancellationLocked: cancellationLocked})
        }
    }, [cancellationLocked]);

    return (
        <>

            {/*{JSON.stringify(formData)}*/}

            <Col id={"mainRequestList"}>

                <Row>
                    <Col xs={12} sm={6} md={6} lg={6} className={"mb-4"}>
                        <Form.Group controlId={"name"}>
                            <Form.Label>
                                Nombre
                                &nbsp;<FormLabelRequiredAsterisk/>
                            </Form.Label>
                            <Form.Control
                                type="text"
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({
                                    ...formData,
                                    name: e.target.value
                                })}
                                required
                                value={formData.name}
                                isInvalid={!!validationErrors.name}
                            />
                        </Form.Group>
                    </Col>

                    <Col xs={12} sm={6} md={6} lg={6} className={"mb-4"}>
                        <Form.Group controlId={"type"}>
                            <Form.Label>
                                Tipo
                                &nbsp;<FormLabelRequiredAsterisk/>
                            </Form.Label>
                            {props.isEdit && <h5>{ACTIVITY_TYPE_LABEL_BY_IDS[formData.type]}</h5>}

                            {!props.isEdit &&
                                <ActivityTypeSelect
                                    onChange={(option: any) => setFormData({...formData, type: option?.value ?? ""})}
                                    value={formData.type}
                                    classes={!!validationErrors.type ? 'is-invalid' : ''}
                                />
                            }
                        </Form.Group>
                    </Col>

                    <Col xs={12} sm={6} md={6} lg={6} className={"mb-4"}>
                        <Form.Group controlId={"category"}>
                            <Form.Label>
                                Categoría
                                &nbsp;<FormLabelRequiredAsterisk/>
                            </Form.Label>
                            <ActivityCategorySelect
                                onChange={(option: any) => setFormData({...formData, category: option?.value ?? ""})}
                                value={formData.category}
                                classes={!!validationErrors.category ? 'is-invalid' : ''}
                            />
                        </Form.Group>
                    </Col>

                    <Col xs={12} sm={6} md={6} lg={6} className={"mb-4"}>
                        <Form.Group controlId={"paymentType"}>
                            <Form.Label>
                                Tipo de pago
                            </Form.Label>
                            <PaymentTypeSelect
                                onChange={(option: any) => setFormData({...formData, paymentType: option?.value ?? null})}
                                defaultPaymentTypeId={formData.paymentType?.id ?? formData.paymentType}
                            />
                        </Form.Group>
                    </Col>

                    <Col xs={12} sm={6} md={6} lg={6} className={"mb-4"}>
                        <Form.Label>
                            Centro/s
                            &nbsp;<FormLabelRequiredAsterisk/>
                        </Form.Label>
                        <CenterMultiSelect
                            onChange={(options: any) => {
                                let optionValues: string[] = []
                                options.map(opt => optionValues.push(opt.value))

                                let newFormData = formData
                                newFormData = {...newFormData, centerIds: optionValues ?? []}
                                newFormData = {...newFormData, centers: options}
                                setFormData(newFormData)
                            }}
                            values={formData.centers}
                            classes={!!validationErrors.centerIds ? 'is-invalid' : ''}
                        />
                    </Col>

                    <Col xs={12} sm={6} md={6} lg={6} className={"mb-4 align-self-end"}>
                        <Form.Label>
                            Descuento/s
                        </Form.Label>
                        <DiscountMultiSelect
                            onChange={(options: any) => {
                                let optionValues: string[] = []
                                options.map(opt => optionValues.push(opt.value))

                                let newFormData = formData
                                newFormData = {...newFormData, discountIds: optionValues ?? []}
                                newFormData = {...newFormData, discounts: options}
                                setFormData(newFormData)
                            }}
                            values={formData.discounts}
                        />
                    </Col>

                    <Col xs={12} sm={6} md={6} lg={6} className={"mb-4"}>
                        <Form.Group controlId={"startDate"}>
                            <Form.Label>
                                Fecha de inicio
                                &nbsp;<FormLabelRequiredAsterisk/>
                            </Form.Label>
                            <Form.Control
                                type="date"
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({
                                    ...formData,
                                    initDate: e.target.value
                                })}
                                required
                                value={formData.initDate}
                                isInvalid={!!validationErrors.initDate}
                            />
                        </Form.Group>
                    </Col>

                    <Col xs={12} sm={6} md={6} lg={6} className={"mb-4"}>
                        <Form.Group controlId={"endDate"}>
                            <Form.Label>
                                Fecha fin
                                &nbsp;<FormLabelRequiredAsterisk/>
                            </Form.Label>
                            <Form.Control
                                type="date"
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({
                                    ...formData,
                                    endDate: e.target.value
                                })}
                                required
                                value={formData.endDate}
                                isInvalid={!!validationErrors.endDate}
                            />
                        </Form.Group>
                    </Col>

                    <Col xs={12} sm={6} md={6} lg={6} className={"mb-4"}>
                        <Form.Group controlId={"startDate"}>
                            <Form.Label>
                                Fecha de pago inicial
                                &nbsp;<FormLabelRequiredAsterisk/>
                            </Form.Label>
                            <Form.Control
                                type="date"
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({
                                    ...formData,
                                    initPaymentDate: e.target.value
                                })}
                                required
                                value={formData.initPaymentDate}
                                isInvalid={!!validationErrors.initPaymentDate}
                            />
                        </Form.Group>
                    </Col>

                    <Col xs={12} sm={6} md={6} lg={6} className={"mb-4"}>
                        <Form.Group controlId={"endDate"}>
                            <Form.Label>
                                Fecha de pago final
                                &nbsp;<FormLabelRequiredAsterisk/>
                            </Form.Label>
                            <Form.Control
                                type="date"
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({
                                    ...formData,
                                    endPaymentDate: e.target.value
                                })}
                                required
                                value={formData.endPaymentDate}
                                isInvalid={!!validationErrors.endPaymentDate}
                            />
                        </Form.Group>
                    </Col>
                </Row>
                <Row className={"my-4"}>
                    <Col>
                        <Form.Group
                            controlId={"cancellationLocked"}
                            className={"mb-4"}
                        >
                            <label
                                htmlFor={"cancellationLocked"}
                                className={"d-block mb-2 fw-bold pointer"}
                            >
                                Bloquear cancelaciones
                            </label>
                            <Toggle
                                id={"cancellationLocked"}
                                checked={cancellationLocked}
                                onChange={() => setCancellationLocked(!cancellationLocked)}
                            />
                        </Form.Group>
                    </Col>
                    <Col>
                        <Form.Group
                            controlId={"canSelectMultipleYearDays"}
                            className={"mb-4"}
                        >
                            <label
                                htmlFor={"canSelectMultipleYearDays"}
                                className={"d-block mb-2 fw-bold pointer"}
                            >
                                Puede seleccionar múltiples días
                            </label>
                            <Toggle
                                id={"canSelectMultipleYearDays"}
                                checked={canSelectMultipleYearDays}
                                onChange={() => {
                                    const value = !canSelectMultipleYearDays
                                    setCanSelectMultipleYearDays(value)
                                    setFormData({...formData, canSelectMultipleYearDays: value})
                                }}
                            />
                        </Form.Group>
                    </Col>
                    <Col>
                        <Form.Group
                            controlId={"quarterlyPayment"}
                            className={"mb-4"}
                        >
                            <label
                                htmlFor={"quarterlyPayment"}
                                className={"d-block mb-2 fw-bold pointer"}
                            >
                                Pago trimestral
                            </label>
                            <Toggle
                                id={"quarterlyPayment"}
                                checked={quarterlyPayment}
                                onChange={() => {
                                    const value = !quarterlyPayment
                                    setQuarterlyPayment(value)
                                    setFormData({...formData, quarterlyPayment: value})
                                }}
                            />
                            <Form.Text className={"d-block"}>
                                La inscripción solo aparecerá en el informe de recibos en enero, abril, julio y octubre.
                            </Form.Text>
                        </Form.Group>
                    </Col>

                    { formData.type === ACTIVITY_TYPE_WEEKLY_ID &&
                    <Col>
                        <label className={"d-block mb-2 fw-bold pointer"}>
                            Tipo de validación de inscripciones
                        </label>
                        <InscriptionValidationTypeSelect
                            onChange={(option:any) => setFormData({...formData, inscriptionValidationType: option})}
                            value={formData.inscriptionValidationType}
                        />
                    </Col>
                    }

                </Row>
                <Row className={"pt-4"}>
                    <Col>
                        <Form.Group controlId={"description"}>
                            <Form.Label>
                                Descripción
                                &nbsp;<FormLabelRequiredAsterisk/>
                            </Form.Label>

                            <RichTextEditor
                                onChange={(htmlContent) => {
                                    setFormData({...formData, description: htmlContent})
                                }}
                                defaultContent={formData.description}
                            />
                        </Form.Group>

                    </Col>
                </Row>
                <Row className={"mt-4"}>
                    <Col xs={12} sm={6} md={4} lg={4}>
                        <Form.Group controlId={"frontImage"}>
                            <Form.Label>
                                Imagen de portada
                                &nbsp;<FormLabelRequiredAsterisk/>
                            </Form.Label>
                            <div className={!!validationErrors.frontImage ? 'is-invalid-div' : ''}>
                                {
                                    formData?.frontImageSrc &&
                                    <Row className={"mb-4"}>
                                        <img src={formData?.frontImageSrc} height={250}
                                             style={{"maxWidth": '100%'}}/>
                                    </Row>
                                }

                                <FileUpload
                                    onChange={(files) => files ? setFormData({
                                        ...formData,
                                        frontImage: files[0]
                                    }) : null}
                                    maxFiles={1}
                                />
                            </div>
                        </Form.Group>
                    </Col>
                    <Col xs={12} sm={6} md={4} lg={4}>
                        <Form.Group controlId={"backgroundImage"}>
                            <Form.Label>
                                Imagen de fondo
                            </Form.Label>
                            {
                                formData?.backgroundImageSrc &&
                                <Row className={"mb-4"}>
                                    <img src={formData?.backgroundImageSrc} height={250}
                                         style={{"maxWidth": '100%'}}/>
                                </Row>
                            }
                            <FileUpload
                                onChange={(files) => files ? setFormData({
                                    ...formData,
                                    backgroundImage: files[0]
                                }) : null}
                                maxFiles={1}
                            />
                        </Form.Group>
                    </Col>
                    <Col xs={12} sm={6} md={4} lg={4}>
                        <Form.Group controlId={"bodyImage"}>
                            <Form.Label>
                                Imagen previa a la descripción
                            </Form.Label>
                            {
                                formData?.bodyImageSrc &&
                                <>
                                    <Row className={"mb-4"}>
                                        <img src={formData?.bodyImageSrc} height={250}
                                             style={{"maxWidth": '100%'}}/>
                                    </Row>
                                    <Row>
                                        {formData.bodyImage && <div className="pb-4">
                                            <Button
                                                className={"d-block w-100 btn-danger"}
                                                size={"sm"}
                                                onClick={() => removeImage(formData.bodyImageId)}
                                            >
                                                Eliminar
                                            </Button>
                                        </div>}
                                    </Row>
                                </>
                            }

                            {
                                !formData?.bodyImageSrc &&
                                <FileUpload
                                    onChange={(files) => files ? setFormData({
                                        ...formData,
                                        bodyImage: files[0]
                                    }) : null}
                                    maxFiles={1}
                                />
                            }

                        </Form.Group>
                    </Col>
                </Row>

                <Row className={"mt-4"}>
                    <Col xs={12}>
                        <Form.Group controlId={"documents"}>
                            <Form.Label>
                                <strong>Documentos</strong>
                                <p>Aquí puedes adjuntar documentos para que los usuarios puedan visualizarlos o
                                    descargarlos.</p>
                            </Form.Label>
                            <FileUpload
                                onChange={(files) => files ? setFormData({
                                    ...formData,
                                    documents: files
                                }) : null}
                                maxFiles={50}
                            />
                        </Form.Group>

                        <div className={"my-4"}>
                            <strong>Documentos subidos</strong>
                            <ul className={"mt-2"}>
                                {
                                    Object.values(formData.documents)?.map((file: any) =>
                                        <>
                                            {
                                                file?.url && file?.id &&
                                                <li>
                                                    <a target={"_blank"} href={file.url}>{file.name}</a>
                                                    &nbsp;&nbsp;&nbsp;&nbsp;
                                                    <span
                                                        className={"text-danger ml-2 pointer"}
                                                        onClick={() => deleteDocument(file.id)}
                                                    >Eliminar</span>
                                                </li>
                                            }
                                        </>
                                    )
                                }
                                {
                                    !Object.values(formData.documents).length &&
                                    <li>No hay documentos subidos.</li>
                                }
                            </ul>
                        </div>
                    </Col>
                </Row>

                {
                    formData.type === ACTIVITY_TYPE_YEARLY_ID &&
                    <>
                        <Row className={"mt-4"}>
                            <Col xs={12} sm={12}>
                                <AdminYearDayForm
                                    onChange={(yearDays: AddYearSubmitDto[]) => setFormData({
                                        ...formData,
                                        yearDays: yearDays
                                    })}
                                    yearDays={formData.yearDays}
                                    services={centerServices}
                                />
                            </Col>
                        </Row>
                    </>
                }

                {
                    formData.type === ACTIVITY_TYPE_WEEKLY_ID && props.isEdit &&
                    <>
                        <br/>
                        <Row className={"mt-4"}>
                            <Col xs={12}>
                                <Form.Group controlId={"sessions"}>
                                    <Row className={"d-flex"}>
                                        <Col sm={12} md={6}>
                                            <h3>Actividades</h3>
                                        </Col>
                                        <Col sm={12} md={6} style={{"textAlign": "right"}}>
                                            <Button
                                                className={"purple-btn btn-sm float-right"}
                                                href={ROUTE_PATHS.ADMIN_MODALITY_CREATE + (props.activityId ? '?activityId=' + props.activityId : '')}
                                            >
                                                Crear nueva actividad
                                            </Button>
                                        </Col>
                                    </Row>

                                    <table className={"table table-bordered redTable"}>
                                        <thead>
                                        <tr>
                                            <th>Nombre</th>
                                            <th>Centros</th>
                                            <th>Cursos</th>
                                            <th>Días</th>
                                            <th>Horario</th>
                                            <th>Precio</th>
                                            <th></th>
                                        </tr>

                                        {
                                            formData.sessions.length === 0 &&
                                            <tr>
                                                <td colSpan={5}>
                                                    Todavía no has creado ninguna modalidad para esta inscripción.
                                                </td>
                                            </tr>
                                        }
                                        {
                                            formData.sessions.map((activitySession: any, key: number) => {
                                                return (
                                                    <tr key={key}>
                                                        <td>{activitySession.name}</td>
                                                        <td>{activitySession.centers.map((center, index) => <>{center.name}{index < activitySession.centers.length - 1 && ','}</>)}</td>
                                                        <td>{activitySession.courseLevels?.map((level: any, index: number) => <span key={index}>{level.name}{index < activitySession.courseLevels.length - 1 && ', '}</span>)}
                                                        </td>
                                                        <td>
                                                            {activitySession.days.map((day: any, key: number) => {
                                                                return (
                                                                    <span key={key}>
                                                                        {day.name}
                                                                        {key < activitySession.days.length - 1 && ', '}
                                                                    </span>
                                                                )
                                                            })}
                                                        </td>
                                                        <td>
                                                            <Time value={activitySession.start}/> - <Time
                                                            value={activitySession.end}/>
                                                        </td>
                                                        <td>
                                                            <MoneyAmount amount={activitySession.price}/>
                                                        </td>
                                                        <td>
                                                            <Button
                                                                className={"purple-btn btn-sm"}
                                                                href={ROUTE_PATHS.ADMIN_MODALITY_EDIT.replace(":id", activitySession.id)}
                                                            >
                                                                Modificar
                                                            </Button>
                                                        </td>
                                                    </tr>
                                                )
                                            })
                                        }

                                        </thead>
                                    </table>

                                </Form.Group>
                            </Col>
                        </Row>
                        <br/>
                    </>
                }

            </Col>

            <Row>
                <Col className={"mt-4"}>
                    <br/>
                    <Button
                        className={"purple-btn btn-sm"}
                        onClick={() => submit()}
                    >
                        Enviar
                    </Button>
                </Col>
            </Row>
        </>
    )
}
